import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SocialProfile } from 'src/api/v1-api';
import { socialNetworks } from 'src/utils/marketplaceUtils';

interface SocialProfileFormProps {
  socialProfile: Partial<SocialProfile> | null;
  onCancel: () => void;
  onUpdateSocialProfile: (data: SocialProfile) => void;
  onCreateSocialProfile: (data: Partial<SocialProfile>) => void;
}

interface SocialProfileFormFields {
  network: string;
  username: string;
  url: string;
  total_followers: number;
  show_on_footer_menu: boolean;
  show_on_header_menu: boolean;
}

const SocialProfileForm = ({
  socialProfile,
  onCancel,
  onCreateSocialProfile,
  onUpdateSocialProfile,
}: SocialProfileFormProps) => {
  const defaultValues: SocialProfileFormFields = {
    network: socialProfile?.network || '',
    username: socialProfile?.username || '',
    url: socialProfile?.url || '',
    total_followers: socialProfile?.total_followers || 0,
    show_on_footer_menu: socialProfile?.show_on_footer_menu ?? true,
    show_on_header_menu: socialProfile?.show_on_header_menu ?? true,
  };

  const { control, handleSubmit, reset } = useForm<SocialProfileFormFields>({
    defaultValues,
  });

  useEffect(() => {
    if (socialProfile) {
      reset({
        network: socialProfile?.network || '',
        username: socialProfile?.username || '',
        url: socialProfile?.url || '',
        total_followers: socialProfile?.total_followers || 0,
        show_on_footer_menu: socialProfile?.show_on_footer_menu ?? true,
        show_on_header_menu: socialProfile?.show_on_header_menu ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialProfile]);

  const buildPayload = (data: SocialProfileFormFields): Partial<SocialProfile> => {
    return {
      ...(socialProfile ?? {}),
      network: data.network,
      username: data.username,
      url: data.url,
      total_followers: data.total_followers,
      show_on_footer_menu: data.show_on_footer_menu,
      show_on_header_menu: data.show_on_header_menu,
    };
  };

  const decorateBeforeSubmitData = (data: SocialProfileFormFields) => {
    const payload = buildPayload(data);

    if (socialProfile?.id) {
      onUpdateSocialProfile(payload as SocialProfile);
    } else {
      onCreateSocialProfile(payload);
    }
  };

  return (
    <Flex direction="column">
      <Flex direction="row" alignItems="center" justifyContent="space-between">
        <Text fontWeight="semibold" pb={4}>
          {socialProfile?.id ? 'Edit' : 'New'} Social Profile
        </Text>
      </Flex>
      <Stack as="form" onSubmit={handleSubmit(decorateBeforeSubmitData)}>
        <Flex width={'100%'} gap={4} direction="column">
          <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
            <Flex width="fit-content">
              <Controller
                name="network"
                control={control}
                render={({ field }) => (
                  <FormControl id="network" isRequired>
                    <FormLabel>Network</FormLabel>
                    <Select placeholder="Select a network" {...field}>
                      {socialNetworks.map((network) => (
                        <option key={network.value} value={network.value}>
                          {network.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Flex>
            <Flex grow={1}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <FormControl id="username">
                    <FormLabel>Username</FormLabel>
                    <Input placeholder="@username" {...field} />
                  </FormControl>
                )}
              />
            </Flex>
          </Flex>
          <Stack>
            <Controller
              name="url"
              control={control}
              render={({ field }) => (
                <FormControl id="url" isRequired>
                  <FormLabel>Link</FormLabel>
                  <Input placeholder="https://..." type="url" {...field} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <Controller
              name="total_followers"
              control={control}
              render={({ field }) => (
                <FormControl id="total_followers">
                  <FormLabel>Follower Count</FormLabel>
                  <Input placeholder="10..." type="number" {...field} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <Controller
              name="show_on_header_menu"
              control={control}
              render={({ field }) => (
                <FormControl id="show_on_header_menu" display="flex" alignItems="center">
                  <FormLabel m={0} mr={4}>
                    Show on Header Menu?
                  </FormLabel>
                  <Switch
                    size="md"
                    isChecked={field.value}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </FormControl>
              )}
            />
          </Stack>
          <Stack width="fit-content">
            <Controller
              name="show_on_footer_menu"
              control={control}
              render={({ field }) => (
                <FormControl id="show_on_footer_menu" display="flex" alignItems="center">
                  <FormLabel m={0} mr={4}>
                    Show on Footer Menu?
                  </FormLabel>
                  <Switch
                    size="md"
                    isChecked={field.value}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </FormControl>
              )}
            />
          </Stack>
          <Flex direction="row" alignItems="center" mb={4}>
            <Flex
              direction="row"
              alignItems="center"
              gap={4}
              justifyContent="flex-end"
              flexGrow="1"
            >
              <Button type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="primary" type="submit">
                Save
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Stack>
    </Flex>
  );
};

export default SocialProfileForm;
